import { lazy, Suspense, useMemo, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { useCourse, type AreaRow } from '../data/geo.ts'
import { fullCoverage, jobsByArea, workCounts, workFeatures, type AreaWorkState } from '../data/mapJobs.ts'
import { lookFamily } from '@gk/contracts'
import { paramSummary, settingLines, usePlan, type PlanJob, type TaskTypeRow } from '../data/plan.ts'
import { dayLookFeatures } from '../data/lookMap.ts'
import { frameFor } from '../data/lookGeometry.ts'
import { usePlayLines, type PlayLine } from '../data/playLine.ts'
import type { GeoJsonGeometry } from '../lib/geo.ts'
import { LookStrip } from '../ui/looks/LookStrip.tsx'
import { OutlineSchema } from '../ui/looks/OutlineSchema.tsx'
import { PlayLinePanel } from './course/PlayLinePanel.tsx'
import { useCompletions, useCoverage } from '../data/work.ts'
import { boundsOf } from '../lib/geo.ts'
import { formatLocal, localDate } from '../lib/time.ts'
import { AREA_COLORS, AREA_LEGEND, INFRA_COLORS, INFRA_LEGEND, WORK_LEGEND } from '../map/style.ts'
import { useBaseMap } from '../map/useBaseMap.ts'
import type { MapLabel, Selection } from '../map/MapView.tsx'
import type { LiveMarker } from '../map/LiveLayer.tsx'
import { freshRows, initials, liveTitle, minutesAgo, MY_MARKER_TONE, onePerPerson, toneFor, useLivePositions } from '../data/liveMap.ts'
import { useLocationOn } from '../data/location.ts'
import { AreaPanel } from './map/AreaPanel.tsx'
import { InfraPanel } from './map/InfraPanel.tsx'
import { useWriteAccess } from '../ui/hooks.ts'
import { useNow } from '../ui/useNow.ts'
import type { LivePosition } from '@gk/contracts'

const MapView = lazy(() => import('../map/MapView.tsx'))
// the live layer needs the map library: it rides in MapView's own lazy chunk
const LiveLayer = lazy(() => import('../map/MapView.tsx').then((m) => ({ default: m.LiveLayer })))
/** "165 Ko" under a megabyte, "52 Mo" above (the unit French readers expect). */
export const fileSize = (bytes: number, lang: string): string => {
  const n = (v: number) => new Intl.NumberFormat(lang, { maximumFractionDigits: v < 10 ? 1 : 0 }).format(v)
  return bytes < 1_048_576 ? `${n(Math.max(1, bytes / 1024))} ${lang.startsWith('en') ? 'KB' : 'Ko'}` : `${n(bytes / 1_048_576)} ${lang.startsWith('en') ? 'MB' : 'Mo'}`
}
const mb = fileSize

/** The course on a map: the aerial photo kept on this device, the club's areas and its underground services, with what is known about each (and how
 *  well it is known). Opens with no network once the map has been downloaded. */
export function MapPage() {
  const { t, i18n } = useTranslation()
  const { site, can, kiosk, personId } = useClub()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { area?: string; infra?: string }
  const course = useCourse(i18n.language)
  const base = useBaseMap()
  const editInfra = useWriteAccess('infra.edit')
  const editAreas = useWriteAccess('area.edit')
  const publish = useWriteAccess('map.publish')
  const [showAreas, setShowAreas] = useState(true)
  const [showInfra, setShowInfra] = useState(true)
  // M16-E: today's work, the areas coloured by what remains to be done on them (data/mapJobs.ts); off until asked, so the map opens as it always did
  const [showWork, setShowWork] = useState(false)
  // M25-L: « Sens du jour », the axis of the day's direction or pattern across each area on its line of play (data/lookMap.ts); off until asked
  const [showLooks, setShowLooks] = useState(false)
  const [legend, setLegend] = useState(false)
  const [picked, setPicked] = useState<Selection>(null)
  // M19: who is where. Everybody who works on the course sees it (the server decides: api.live_positions asks for job.execute); never a workstation, and
  // never where the club has location off at the site (M21): no live layer, no toggle, no list; the map itself stays
  const location = useLocationOn()
  const liveAllowed = can('job.execute') && !kiosk && location
  const [showLive, setShowLive] = useState(true)
  const live = useLivePositions(liveAllowed && showLive)
  const now = useNow(15_000)
  const people = useMemo(() => onePerPerson(freshRows(live.rows ?? [], now)), [live.rows, now])
  const [pickedPerson, setPickedPerson] = useState<string | null>(null)
  const markers = useMemo<LiveMarker[]>(() => people.map((p) => ({
    id: p.person_id, lon: p.lon, lat: p.lat, initials: initials(p.display_name), tone: p.person_id === personId ? MY_MARKER_TONE : toneFor(p.person_id), me: p.person_id === personId,
    label: p.display_name, picked: p.person_id === pickedPerson,
  })), [people, personId, pickedPerson])
  const pickedRow = people.find((p) => p.person_id === pickedPerson) ?? null
  // the day's jobs per area, from the device's copy (the board's own rows): the work layer and the area popup read the same
  const today = localDate(now, site.timezone)
  const { plan, taskTypes } = usePlan(i18n.language)
  const play = usePlayLines()
  const completions = useCompletions(); const coverage = useCoverage()
  const byArea = useMemo(() => jobsByArea(plan.jobs, plan.jobs.flatMap((j) => j.areaIds.map((area_id) => ({ job_id: j.id, area_id, removed_by_command_id: null }))), today), [plan.jobs, today])
  const covered = useMemo(() => fullCoverage(completions ?? [], coverage ?? []), [completions, coverage])
  const work = useMemo(() => workFeatures(course.areaCollection, byArea, covered), [course.areaCollection, byArea, covered])
  const geoms = useMemo(() => new Map(course.areaCollection.features.map((f) => [f.id, f.geometry])), [course.areaCollection])
  const looks = useMemo(() => dayLookFeatures(byArea, taskTypes, geoms, play?.lines ?? new Map()), [byArea, taskTypes, geoms, play])
  const selected: Selection = picked ?? (search.infra ? { kind: 'infra', id: search.infra } : search.area ? { kind: 'area', id: search.area } : null)

  const fit = useMemo(() => boundsOf([...course.areaCollection.features.map((f) => f.geometry), ...course.infraCollection.features.map((f) => f.geometry)]), [course.areaCollection, course.infraCollection])
  // a hole's number sits on its green (or, failing that, on whatever of it is drawn)
  const labels = useMemo<MapLabel[]>(() => {
    const geomOf = new Map(course.areaCollection.features.map((f) => [f.id, f.geometry]))
    const out: MapLabel[] = []
    for (const hole of course.areas) {
      if (hole.area_type_code !== 'hole' || hole.hole_number == null || hole.archived_at) continue
      const kids = course.areas.filter((a) => a.parent_id === hole.id && geomOf.has(a.id))
      const kid = kids.find((k) => k.area_type_code === 'green') ?? kids[0]
      const b = kid ? boundsOf([geomOf.get(kid.id)!]) : null
      if (b) out.push({ id: hole.id, lon: (b[0] + b[2]) / 2, lat: (b[1] + b[3]) / 2, text: String(hole.hole_number) })
    }
    return out
  }, [course.areas, course.areaCollection])

  const sel = useMemo(() => {
    if (!selected) return null
    if (selected.kind === 'area') {
      const area = course.areas.find((a) => a.id === selected.id)
      const version = course.areaVersions.find((v) => v.area_id === selected.id && /,\s*\)$/.test(v.valid_during))
      if (!area) return null
      const path: AreaRow[] = []
      for (let p = course.areas.find((a) => a.id === area.parent_id); p && p.parent_id; p = course.areas.find((a) => a.id === p!.parent_id)) path.unshift(p)
      return { kind: 'area' as const, area, version, path }
    }
    const item = course.infra.find((i) => i.id === selected.id)
    const version = course.infraVersions.find((v) => v.feature_id === selected.id && /,\s*\)$/.test(v.valid_during))
    return item ? { kind: 'infra' as const, item, version } : null
  }, [selected, course])
  const select = (s: Selection) => { setPicked(s); if (search.area || search.infra) void navigate({ to: '/map', search: {}, replace: true }) }

  return (
    <section className="flex flex-col gap-3" data-testid="map-page">
      {/* every control of this page is a 56 px target (the M18 exception is for the week grid only) */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">{t('map.title')}</h1>
        <div className="flex flex-wrap gap-2">
          <Link to="/course" className="btn !px-3" data-testid="go-course">{t('map.course')}</Link>
          {editInfra.allowed && <Link to="/map/infra/new" className="btn btn-primary !px-3" data-testid="infra-add">{t('map.addInfra')}</Link>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('map.layers')}>
        <button className={`btn !px-3 ${showAreas ? 'btn-primary' : ''}`} aria-pressed={showAreas} onClick={() => setShowAreas(!showAreas)} data-testid="toggle-areas">{t('map.areas')}</button>
        <button className={`btn !px-3 ${showInfra ? 'btn-primary' : ''}`} aria-pressed={showInfra} onClick={() => setShowInfra(!showInfra)} data-testid="toggle-infra">{t('map.infra')}</button>
        <button className={`btn !px-3 ${showWork ? 'btn-primary' : ''}`} aria-pressed={showWork} onClick={() => setShowWork(!showWork)} data-testid="toggle-work">{t('map.work.toggle')}</button>
        <button className={`btn !px-3 ${showLooks ? 'btn-primary' : ''}`} aria-pressed={showLooks} onClick={() => setShowLooks(!showLooks)} data-testid="toggle-looks">{t('map.looks.toggle')}</button>
        {liveAllowed && <button className={`btn !px-3 ${showLive ? 'btn-primary' : ''}`} aria-pressed={showLive} onClick={() => setShowLive(!showLive)} data-testid="live-layer">{t('live.layer')}</button>}
        <button className="btn !px-3" aria-expanded={legend} onClick={() => setLegend(!legend)} data-testid="toggle-legend">{t('map.legend')}</button>
      </div>
      {liveAllowed && showLive && !live.online && <p className="rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-100" role="status" data-testid="live-offline">{t('live.offline')}</p>}
      <BaseStatus base={base} lang={i18n.language} canPublish={publish.permitted} />
      {legend && <Legend />}

      <Suspense fallback={<div className="grid h-[60dvh] place-items-center rounded-xl border">{t('common.loading')}</div>}>
        <MapView className="h-[68dvh] min-h-80 overflow-hidden rounded-xl border-2" base={base.archive} areas={course.areaCollection} infra={course.infraCollection} work={work} showWork={showWork} looks={looks.collection} showLooks={showLooks}
          showAreas={showAreas} showInfra={showInfra} labels={labels} selected={selected} onSelect={select} fit={fit}>
          {liveAllowed && showLive && <Suspense fallback={null}><LiveLayer people={markers} onPick={setPickedPerson} /></Suspense>}
        </MapView>
      </Suspense>
      <p className="meta" data-testid="map-counts">{t('map.counts', { areas: course.areaCollection.features.length, infra: course.infraCollection.features.length })}</p>
      {/* release b review (finding 4): the day's legend goes UNDER the map, so the map keeps its place when the layer is turned on (above it, it pushed the colours
          the person asked for below the fold at phone width) */}
      {showWork && <WorkLegend counts={workCounts(work)} today={today} />}
      {showLooks && <LooksLegend drawn={looks.drawn.length} leftOut={looks.leftOut.length} />}
      {liveAllowed && showLive && live.online && (
        <LiveList people={people} me={personId} picked={pickedPerson} onPick={setPickedPerson} now={now} lang={i18n.language} tz={site.timezone} loaded={live.rows !== null} error={live.error} loadedAt={live.loadedAt} pickedRow={pickedRow} />
      )}

      {sel ? (
        <div className="card flex flex-col gap-2" data-testid="map-info">
          {sel.kind === 'area'
            ? <>
              <AreaPanel area={sel.area} version={sel.version} path={sel.path} areas={course.areas} byArea={byArea} lang={i18n.language} tz={site.timezone} canEdit={editAreas.allowed} />
              <AreaLooks jobs={byArea.get(sel.area.id) ?? []} taskTypes={taskTypes} geom={geoms.get(sel.area.id)} line={play?.lines.get(sel.area.id)} areaType={sel.area.area_type_code} lang={i18n.language} />
              <PlayLinePanel areaId={sel.area.id} />
            </>
            : <InfraPanel item={sel.item} version={sel.version} versions={course.infraVersions} lang={i18n.language} tz={site.timezone} canEdit={editInfra.allowed} />}
          <button className="btn self-start" onClick={() => select(null)}>{t('common.close')}</button>
        </div>
      ) : <p className="text-sm opacity-70">{t('map.tapHelp')}</p>}
    </section>
  )
}

function BaseStatus({ base, lang, canPublish }: { base: ReturnType<typeof useBaseMap>; lang: string; canPublish: boolean }) {
  const { t } = useTranslation()
  if (base.loading) return null
  const pct = base.progress ? Math.round((base.progress.done / Math.max(1, base.progress.total)) * 100) : 0
  const askDownload = (base.state === 'missing' || base.state === 'stale' || base.state === 'error') && base.layer
  return (
    <div className="flex flex-col gap-1" data-testid="map-base-status" data-state={base.state}>
      {base.state === 'none' && <p className="rounded-lg border bg-sky-50 px-3 py-2 text-sm text-sky-900">{t('map.noLayer')} {canPublish && <Link to="/map/publish" className="font-semibold underline">{t('map.publishLink')}</Link>}</p>}
      {askDownload && (
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm ${base.state === 'error' ? 'border-red-300 bg-red-50 text-red-900' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
          <span className="min-w-0 flex-1">{base.state === 'stale' ? t('map.newer') : t('map.notOnDevice')} ({fileSize(base.layer!.bytes, lang)})</span>
          <button className="btn btn-primary !px-3" onClick={() => void base.download()} disabled={!base.online} data-testid="map-download">{t('map.download')}</button>
          {base.error && <span className="w-full font-semibold" role="alert" data-testid="map-download-error">{t(`map.error.${base.error}`)}</span>}
        </div>
      )}
      {base.state === 'downloading' && <p className="rounded-lg border bg-sky-50 px-3 py-2 text-sm text-sky-900" data-testid="map-progress">{t('map.downloading', { pct })}</p>}
      {base.state === 'ready' && base.layer && <p className="meta" data-testid="map-ready">{t('map.onDevice', { attribution: base.layer.attribution })}</p>}
    </div>
  )
}

function Legend() {
  const { t } = useTranslation()
  return (
    <div className="card grid grid-cols-2 gap-x-4 gap-y-1 text-sm" data-testid="legend">
      {AREA_LEGEND.map((k) => <span key={k} className="flex items-center gap-2"><i className="inline-block size-3 rounded-sm" style={{ background: AREA_COLORS[k] }} />{t(`areaType.${k}`)}</span>)}
      {INFRA_LEGEND.map((k) => <span key={k} className="flex items-center gap-2"><i className="inline-block size-3 rounded-full" style={{ background: INFRA_COLORS[k] }} />{t(`infraKind.${k}`)}</span>)}
    </div>
  )
}

/** M16-E: the colours of today's work, with how many areas are in each state and the rule that colours them (an area with no job today is not coloured).
 *  The chips are the theme's tokens, the map's own colours (map/style.ts WORK_FILL; map.test holds them to index.css). */
const WORK_CHIP: Record<AreaWorkState, string> = { planned: 'bg-sand border-sand-ink', in_progress: 'bg-fairway border-brand-dark', partially_done: 'bg-sand border-sand-ink', done: 'bg-brand border-brand-dark' }
const STRIPES = { backgroundImage: 'repeating-linear-gradient(135deg, var(--color-brand) 0 3px, transparent 3px 8px)' }
function WorkLegend({ counts, today }: { counts: Record<AreaWorkState, number>; today: string }) {
  const { t } = useTranslation()
  const total = WORK_LEGEND.reduce((n, k) => n + counts[k], 0)
  return (
    <div className="card flex flex-col gap-2" data-testid="work-legend" data-day={today} data-total={total}>
      <h2 className="font-bold">{t('map.work.title', { date: today.split('-').reverse().join('/') })}</h2>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {WORK_LEGEND.map((k) => (
          <span key={k} className="flex items-center gap-2" data-testid="work-legend-item" data-state={k} data-count={counts[k]}>
            <i className={`inline-block size-4 shrink-0 rounded-sm border-2 ${WORK_CHIP[k]}`} style={k === 'partially_done' ? STRIPES : undefined} />{t(`map.work.state.${k}`)} ({counts[k]})
          </span>
        ))}
      </div>
      {total === 0 && <p className="meta" data-testid="work-none">{t('map.work.none')}</p>}
      <details data-testid="work-rule">
        <summary className="flex min-h-14 cursor-pointer items-center font-semibold" data-testid="work-rule-toggle">{t('map.work.ruleTitle')}</summary>
        <p className="meta">{t('map.work.rule')}</p>
      </details>
    </div>
  )
}

/** The people on the course under the map: a row each (name, task, machine, how fresh, how precise); a tap on a marker or a row picks the person. */
function LiveList({ people, me, picked, onPick, now, lang, tz, loaded, error, loadedAt, pickedRow }: {
  people: LivePosition[]; me: string | null; picked: string | null; onPick: (id: string | null) => void; now: number; lang: string; tz: string; loaded: boolean; error: string | null; loadedAt: number | null; pickedRow: LivePosition | null
}) {
  const { t } = useTranslation()
  const ago = (at: string) => { const n = minutesAgo(at, now); return n === 0 ? t('live.now') : t('live.ago', { count: n }) }
  const acc = (m: number) => t('live.accuracy', { m: new Intl.NumberFormat(lang, { maximumFractionDigits: 0 }).format(m) })
  const facts = (p: LivePosition) => [liveTitle(p, lang) ?? t('work.workshopLabour'), p.asset_tag, ago(p.at), acc(p.accuracy_m)].filter(Boolean).join(' · ')
  return (
    <div className="card flex flex-col gap-2" data-testid="live-list" data-count={people.length}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold">{t('live.listTitle', { count: people.length })}</h2>
        {loadedAt && <span className="meta">{t('live.updated', { time: formatLocal(loadedAt, tz, 'HH:mm') })}</span>}
      </div>
      {pickedRow && (
        <div className="flex items-start justify-between gap-3 rounded-xl border-2 border-yellow-300 bg-yellow-50 p-3 text-yellow-950 dark:border-yellow-500/60 dark:bg-yellow-950/30 dark:text-yellow-50" data-testid="live-info" data-person={pickedRow.person_id}>
          <span className="flex min-w-0 flex-col">
            <span className="font-semibold">{pickedRow.person_id === me ? `${pickedRow.display_name} · ${t('live.me')}` : pickedRow.display_name}</span>
            <span className="text-sm">{facts(pickedRow)}</span>
          </span>
          <button className="btn !px-3" onClick={() => onPick(null)}>{t('common.close')}</button>
        </div>
      )}
      {error && <p className="text-sm text-red-800 dark:text-red-300" role="alert" data-testid="live-error">{t('live.error')}</p>}
      {loaded && people.length === 0 && !error && <p className="text-sm opacity-75" data-testid="live-none">{t('live.none')}</p>}
      {people.length > 0 && (
        <ul className="flex flex-col gap-1">
          {people.map((p) => (
            <li key={p.person_id}>
              <button className={`flex min-h-14 w-full items-center gap-3 rounded-xl px-2 py-1 text-left active:bg-fairway-light dark:active:bg-night ${picked === p.person_id ? 'bg-yellow-50 dark:bg-yellow-950/30' : ''}`}
                onClick={() => onPick(picked === p.person_id ? null : p.person_id)} data-testid="live-person" data-person={p.person_id} aria-pressed={picked === p.person_id}>
                <span aria-hidden className={`flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-white text-sm font-bold text-white shadow ${p.person_id === me ? `${MY_MARKER_TONE} ring-2 ring-yellow-300` : toneFor(p.person_id)}`}>{initials(p.display_name)}</span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-semibold">{p.person_id === me ? `${p.display_name} · ${t('live.me')}` : p.display_name}</span>
                  <span className="truncate text-sm opacity-80">{facts(p)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="meta">{t('live.help')}</p>
    </div>
  )
}

/** M25-L: what « Sens du jour » drew, and the areas it left out because their line of play is unknown (rule 6: said, never guessed). */
function LooksLegend({ drawn, leftOut }: { drawn: number; leftOut: number }) {
  const { t } = useTranslation()
  return (
    <div className="card flex flex-col gap-1" data-testid="looks-legend" data-drawn={drawn} data-left-out={leftOut}>
      <h2 className="font-bold">{t('map.looks.title')}</h2>
      <p className="meta">{t('map.looks.help')}</p>
      {drawn === 0 && leftOut === 0 && <p className="text-sm" data-testid="looks-none">{t('map.looks.none')}</p>}
      {drawn > 0 && <p className="text-sm" data-testid="looks-drawn">{t('map.looks.drawn', { count: drawn })}</p>}
      {leftOut > 0 && <p className="text-sm" data-testid="looks-left-out">{t('map.looks.leftOut', { count: leftOut })}</p>}
    </div>
  )
}

/** M25-L: the area popup's pictures — each of the day's jobs on THIS area whose settings carry a picture: its real outline turned to its line of play with
 *  the direction or pattern laid on it (the generic drawing, said, where the line is unknown), its pictures and its settings in the club's words. */
function AreaLooks({ jobs, taskTypes, geom, line, areaType, lang }: { jobs: readonly PlanJob[]; taskTypes: readonly TaskTypeRow[]; geom: GeoJsonGeometry | undefined; line: PlayLine | undefined; areaType: string; lang: string }) {
  const { t } = useTranslation()
  const rows = jobs.filter((j) => j.state !== 'cancelled').map((j) => {
    const tt = taskTypes.find((x) => x.code === j.task_type_code)
    const lines = settingLines(j.params ?? {}, tt, lang)
    return { j, tt, lines, axis: lines.find((l) => l.look && (lookFamily(l.look) === 'dir' || lookFamily(l.look) === 'pat'))?.look ?? null }
  }).filter((r) => r.lines.some((l) => l.look))
  if (!rows.length) return null
  return (
    <section className="flex flex-col gap-2 border-t border-slate-200 pt-2 dark:border-night-line" data-testid="map-area-looks" data-count={rows.length}>
      <h3 className="text-base font-bold">{t('map.looks.areaTitle')}</h3>
      {rows.map(({ j, tt, lines, axis }) => (
        <div key={j.id} className="flex items-start gap-3" data-testid="map-area-look" data-job={j.id}>
          <span className="text-brand dark:text-fairway"><OutlineSchema geom={geom} bearing={line?.bearing ?? null} code={axis} frame={frameFor([areaType])} playArrow showUnknown className="size-14 shrink-0" /></span>
          <div className="flex min-w-0 flex-col gap-1">
            <span className="font-semibold">{j.title_text} <LookStrip lines={lines} className="text-brand dark:text-fairway" /></span>
            <span className="meta">{paramSummary(j.params ?? {}, tt, lang)}</span>
          </div>
        </div>
      ))}
    </section>
  )
}
